import { create } from "zustand";
import { useEffect } from "react";
import $api from "../../api/axios";
import { getContentDisplay } from "./getContentDisplay";
import { TDocument } from "./types";

type TDocumentResponse = Omit<TDocument, "projectId">;

type TDocumentsState = {
  documents: TDocument[];
  isLoading: boolean;
  uploadingDocuments: string[];
  isDocumentsProcessing: boolean;
  isDocumentsPending: boolean;
  setUploadingDocuments: (documents: string[]) => void;
  fetchDocuments: (projectId: string) => Promise<void>;
  createDocument: (projectId: string, file: File, chapterId?: string) => Promise<void>;
  createWebLink: (projectId: string, url: string, chapterId?: string) => Promise<void>;
  deleteDocument: (projectId: string, documentId: number) => Promise<void>;
  retryJuicing: (projectId: string, documentId: string) => Promise<void>;
  summarizeDocument: (projectId: string, documentId: number) => Promise<void>;
};

export const useDocumentsStore = create<TDocumentsState>((set, getState) => ({
  documents: [],
  isLoading: false,
  uploadingDocuments: [],
  isDocumentsProcessing: false,
  isDocumentsPending: false,
  setUploadingDocuments: (documents: string[]) => set({ uploadingDocuments: documents }),
  fetchDocuments: async (projectId: string) => {
    set({ isLoading: true });
    try {
      const response = await $api.get<{ data: TDocumentResponse[] }>(`/projects/${projectId}/documents`);

      const data = response.data.data;
      const documents: TDocument[] = data.map((doc) => {
        const { extractedContent, summarizedContent } = doc;
        const { fullContentDisplay, summaryContentDisplay } = getContentDisplay({
          extractedContent,
          summarizedContent
        });

        return {
          ...doc,
          projectId,
          fullContentDisplay,
          summaryContentDisplay
        };
      });

      const isDocumentsProcessing = documents.some((doc) => doc.status === "processing");
      const isDocumentsPending = documents.some((doc) => doc.status === "pending");

      set({ documents, isDocumentsProcessing, isDocumentsPending });
      documents.forEach((doc) => {
        if (doc.status === "pending") {
          const updatedAt = new Date(doc.updatedAt);
          const now = new Date();
          const diff = now.getTime() - updatedAt.getTime();
          if (diff > 1000 * 60 * 5) {
            // 5 minutes
            getState().retryJuicing(projectId, doc.id.toString());
          }
        }
      });
    } catch (error) {
      console.error("Failed to fetch documents:", error);
    } finally {
      set({ isLoading: false });
    }
  },
  createDocument: async (projectId: string, file: File, chapterId?: string) => {
    set({ isLoading: true });
    try {
      const url = chapterId
        ? `/projects/${projectId}/documents?chapterId=${chapterId}`
        : `/projects/${projectId}/documents`;
      await $api.post(url, { file }, { headers: { "Content-Type": "multipart/form-data" } });
      await getState().fetchDocuments(projectId);
    } catch (error) {
      console.error("Failed to create document:", error);
    } finally {
      set({ isLoading: false });
    }
  },
  createWebLink: async (projectId: string, url: string, chapterId?: string) => {
    set({ isLoading: true });
    try {
      await $api.post(`/projects/${projectId}/documents/url`, { url, chapterId });
      await getState().fetchDocuments(projectId);
    } catch (error) {
      console.error("Failed to add web link:", error);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },
  deleteDocument: async (projectId: string, documentId: number) => {
    // set({ isLoading: true })
    try {
      const response = await $api.delete(`/projects/${projectId}/documents/${documentId}`);
      if (response.status !== 200) {
        console.error("Failed to delete document");
        return;
      }
      set((state) => ({ documents: state.documents.filter((doc) => doc.id !== documentId) }));
      await getState().fetchDocuments(projectId);
    } catch (error) {
      console.error("Failed to delete document:", error);
    } finally {
      // set({ isLoading: false })
    }
  },
  retryJuicing: async (projectId: string, documentId: string) => {
    set({ isLoading: true });
    try {
      await $api.get(`/projects/${projectId}/documents/${documentId}/retry`);
      await getState().fetchDocuments(projectId);
    } catch (error) {
      console.error("Failed to retry juicing:", error);
    } finally {
      set({ isLoading: false });
    }
  },
  summarizeDocument: async (projectId: string, documentId: number) => {
    try {
      await $api.post(`/projects/${projectId}/documents/${documentId}/summarize`);
      await getState().fetchDocuments(projectId);
    } catch (error) {
      console.error("Failed to summarize document:", error);
      throw error;
    }
  }
}));

export function useDocuments(
  projectId: string,
  setDocuments?: (documents: TDocument[]) => void,
  pollInterval: number | null = null
) {
  const {
    documents,
    isLoading,
    fetchDocuments,
    createDocument,
    createWebLink,
    deleteDocument,
    isDocumentsPending,
    isDocumentsProcessing,
    uploadingDocuments,
    setUploadingDocuments,
    retryJuicing,
    summarizeDocument
  } = useDocumentsStore();

  useEffect(() => {
    if (pollInterval !== null) fetchDocuments(projectId);
  }, [fetchDocuments, projectId, pollInterval]);

  useEffect(() => {
    if (pollInterval && documents.some((doc) => doc.status === "processing" || doc.status === "pending")) {
      const interval = setInterval(() => {
        fetchDocuments(projectId);
      }, pollInterval);

      return () => clearInterval(interval);
    }
  }, [fetchDocuments, projectId, pollInterval, documents]);

  useEffect(() => {
    setDocuments?.(documents);
  }, [documents, setDocuments]);

  return {
    documents,
    isLoading,
    isDocumentsProcessing,
    isDocumentsPending,
    uploadingDocuments,
    setUploadingDocuments,
    retryJuicing,
    createDocument: (file: File, chapterId?: string) => createDocument(projectId, file, chapterId),
    createWebLink: (url: string, chapterId?: string) => createWebLink(projectId, url, chapterId),
    deleteDocument: (documentId: number) => deleteDocument(projectId, documentId),
    summarizeDocument: (documentId: number) => summarizeDocument(projectId, documentId),
    refetchDocuments: () => fetchDocuments(projectId)
  };
}
